import React, { useEffect, useRef, useState } from 'react';
import { getDosages, getRxCUI, getSuggestions, reorderForQuery } from '../../services/rxnavService';
import { inp, lbl, noAutofill, NoneCheckbox } from './shared';

const FREQUENCIES = [
  'Once daily (QD)',
  'Twice daily (BID)',
  'Three times daily (TID)',
  'Four times daily (QID)',
  'Every 4 hours',
  'Every 6 hours',
  'Every 8 hours',
  'Every 12 hours',
  'As needed (PRN)',
  'Weekly',
  'Monthly',
];

export default function Prescriptions({ data, onChange }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [drug, setDrug] = useState(null);
  const [dosages, setDosages] = useState([]);
  const [selectedDosage, setDosage] = useState('');
  const [selectedDosageRxcui, setDosageRxcui] = useState('');
  const [frequency, setFrequency] = useState('');
  const [loadingDrug, setLoadingDrug] = useState(false);
  const [loadingDosage, setLoadingDosage] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualStrength, setManualStrength] = useState('');
  const [manualFreq, setManualFreq] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Field name matches Medicare_Intake's medications field on purpose - see
  // IndFamWizard.js's initialData comment.
  const prescriptions = data.medications || [];
  const canAdd = drug && selectedDosage && frequency;

  useEffect(() => {
    function close(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    setSuggestions(getSuggestions(query));
  }, [query]);

  async function selectDrug(name) {
    setSuggestions([]);
    setQuery('');
    setDosage('');
    setDosageRxcui('');
    setFrequency('');
    setLoadingDrug(true);
    try {
      const rxcui = await getRxCUI(name);
      setDrug({ name, rxcui });
      if (rxcui) {
        setLoadingDosage(true);
        setDosages(await getDosages(name, rxcui));
        setLoadingDosage(false);
      } else {
        setDosages([]);
      }
    } catch {
      setDrug({ name, rxcui: null });
      setDosages([]);
    }
    setLoadingDrug(false);
  }

  function clearEntry() {
    setDrug(null);
    setDosages([]);
    setDosage('');
    setDosageRxcui('');
    setFrequency('');
    setQuery('');
    setSuggestions([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function addPrescription() {
    onChange('medications', [...prescriptions, {
      name: drug.name,
      rxcui: selectedDosageRxcui || drug.rxcui || '',
      strength: selectedDosage,
      frequency,
    }]);
    clearEntry();
  }

  function removePrescription(index) {
    onChange('medications', prescriptions.filter((_, i) => i !== index));
  }

  function addManualPrescription(e) {
    e.preventDefault();
    if (!manualName.trim()) return;
    onChange('medications', [...prescriptions, {
      name: manualName.trim(),
      rxcui: '',
      strength: manualStrength.trim(),
      frequency: manualFreq,
    }]);
    setManualName('');
    setManualStrength('');
    setManualFreq('');
    setManualMode(false);
  }

  return (
    <div>
      <div style={instructionBox}>
        <strong style={{ color: '#1a3364' }}>How to add prescriptions:</strong>
        <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
          <li>Type at least 2 letters and select the medication.</li>
          <li>Choose a strength and frequency.</li>
          <li>Add it to the list and repeat for each prescription.</li>
          <li>Can't find it at all? Check "Can't find your medication?" below the search box to enter it by hand.</li>
        </ol>
      </div>

      <div style={entryBox}>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Prescription Name</label>
          {drug ? (
            <div style={selectedDrug}>
              <div>
                <span style={{ fontWeight: 700, color: '#1a3364', fontSize: 15 }}>{drug.name}</span>
                {drug.rxcui && <span style={{ fontSize: 12, color: '#7a99b8', marginLeft: 8 }}>RxCUI: {drug.rxcui}</span>}
              </div>
              <button type="button" onClick={clearEntry} style={changeBtn}>Change</button>
            </div>
          ) : (
            <div ref={containerRef} style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                style={{ ...inp, background: '#fff' }}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type a prescription name..."
                disabled={loadingDrug}
                {...noAutofill('prescription-search')}
              />
              {loadingDrug && <div style={muted}>Loading...</div>}
              {suggestions.length > 0 && (
                <div style={dropdown}>
                  {suggestions.map(item => (
                    <div
                      key={item}
                      onMouseDown={() => selectDrug(item)}
                      style={dropdownItem}
                      onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      {reorderForQuery(item, query)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {!drug && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="rxManualMode"
                checked={manualMode}
                onChange={e => setManualMode(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1a3364' }}
              />
              <label htmlFor="rxManualMode" style={{ fontSize: 13, color: '#4a6288', cursor: 'pointer' }}>
                Can't find your medication? Enter it manually
              </label>
            </div>
            {manualMode && (
              <div style={manualPanel}>
                <div style={{ marginBottom: 10 }}>
                  <label style={lbl}>Medication Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualName}
                    onChange={e => setManualName(e.target.value)}
                    placeholder="e.g. Hydrocodone/Acetaminophen"
                    {...noAutofill('prescription-manual-name')}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={lbl}>Strength <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualStrength}
                    onChange={e => setManualStrength(e.target.value)}
                    placeholder="e.g. 5 MG / 325 MG"
                    {...noAutofill('prescription-manual-strength')}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={lbl}>Frequency <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <select
                    style={{ ...inp, cursor: 'pointer', appearance: 'auto', background: '#fff', color: '#1a3364' }}
                    value={manualFreq}
                    onChange={e => setManualFreq(e.target.value)}
                  >
                    <option value="">Select a frequency...</option>
                    {FREQUENCIES.map(item => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={addManualPrescription}
                    disabled={!manualName.trim()}
                    style={{
                      padding: '9px 22px', border: 'none', borderRadius: 8,
                      background: manualName.trim() ? '#2a5298' : '#b8cfe8',
                      color: '#fff', fontWeight: 600, fontSize: 14,
                      cursor: manualName.trim() ? 'pointer' : 'default',
                    }}
                  >
                    + Add Prescription
                  </button>
                  <button type="button" onClick={() => setManualMode(false)} style={cancelBtn}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>
            Strength
            {loadingDosage && <span style={{ fontWeight: 400, color: '#7a99b8', marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>Loading...</span>}
          </label>
          <select
            style={{ ...inp, cursor: 'pointer', appearance: 'auto', background: '#fff', color: '#1a3364' }}
            value={selectedDosage}
            onChange={e => {
              const label = e.target.value;
              const match = dosages.find(dosage => dosage.label === label);
              setDosage(label);
              setDosageRxcui(match?.rxcui ?? '');
            }}
            disabled={!drug || loadingDosage}
          >
            <option value="">{!drug ? 'Select a prescription first...' : dosages.length === 0 ? 'No strengths found - type manually below' : 'Select a strength...'}</option>
            {dosages.map(dosage => (
              <option key={dosage.rxcui || dosage.label} value={dosage.label}>{dosage.label}</option>
            ))}
          </select>
          {drug && dosages.length === 0 && !loadingDosage && (
            <input
              style={{ ...inp, background: '#fff', marginTop: 8 }}
              value={selectedDosage}
              placeholder="e.g. 10 mg"
              onChange={e => setDosage(e.target.value)}
              {...noAutofill('prescription-strength')}
            />
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Frequency</label>
          <select
            style={{ ...inp, cursor: 'pointer', appearance: 'auto', background: '#fff', color: '#1a3364' }}
            value={frequency}
            onChange={e => setFrequency(e.target.value)}
            disabled={!drug}
          >
            <option value="">{!drug ? 'Select a prescription first...' : 'Select a frequency...'}</option>
            {FREQUENCIES.map(item => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        <button type="button" onClick={addPrescription} disabled={!canAdd} style={{
          ...addPrescriptionBtn,
          background: canAdd ? '#1a3364' : '#b8cfe8',
          cursor: canAdd ? 'pointer' : 'default',
        }}>
          + Add Prescription
        </button>
      </div>

      {prescriptions.length > 0 && (
        <div>
          <div style={addedHeading}>Added Prescriptions <span style={countPill}>{prescriptions.length}</span></div>
          {prescriptions.map((rx, index) => (
            <div key={index} style={addedRx}>
              <div>
                <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>{rx.name}</div>
                <div style={{ fontSize: 13, color: '#4a6288', marginTop: 2 }}>
                  {rx.strength}
                  {rx.frequency && <span style={{ color: '#7a99b8' }}> - {rx.frequency}</span>}
                </div>
              </div>
              <button type="button" onClick={() => removePrescription(index)} style={removeBtn}>×</button>
            </div>
          ))}
        </div>
      )}

      {prescriptions.length === 0 && (
        <NoneCheckbox
          checked={data.noMedications}
          onChange={v => onChange('noMedications', v)}
          label="I don't take any prescription medications"
        />
      )}
    </div>
  );
}

const instructionBox = { background: '#f4f8fc', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#4a6288', lineHeight: 1.6 };
const entryBox = { background: '#f4f8fc', border: '1.5px solid #dbeef8', borderRadius: 10, padding: '18px 18px 14px', marginBottom: 20 };
const selectedDrug = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, padding: '10px 12px' };
const changeBtn = { padding: '5px 12px', border: '1.5px solid #c5d5e8', borderRadius: 6, background: '#fff', color: '#2a5298', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const muted = { fontSize: 13, color: '#7a99b8', marginTop: 4 };
const dropdown = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2 };
const dropdownItem = { padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364' };
const addPrescriptionBtn = { padding: '10px 24px', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, transition: 'background 0.15s' };
const addedHeading = { fontSize: 13, fontWeight: 700, color: '#1a3364', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 };
const countPill = { background: '#1a3364', color: '#fff', borderRadius: '999px', padding: '1px 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none' };
const addedRx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '11px 14px', marginBottom: 8 };
const removeBtn = { padding: '5px 10px', border: 'none', background: '#f5e8e8', borderRadius: 6, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 14 };
const manualPanel = { background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, padding: 14, marginTop: 10 };
const cancelBtn = { padding: '9px 16px', border: '1.5px solid #c5d5e8', borderRadius: 8, background: '#fff', color: '#4a6288', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
